import { CampaignState } from 'modules/domains/campaign';
import {
  getTakeoverCampaignMessageMedium,
  getTakeoverCampaignState,
  getTakeoverExperimentId,
  getTakeoverExperimentType,
  getTakeoverVariantList,
} from 'modules/experiments/ducks/selectors';
import {
  ExperimentVariationInputType,
  SelectVariantMode,
  UpdateVariantArgs,
  VariantConfigurationContextState,
  VariantFieldValues,
  VariantFormFieldKey,
  VariantIssue,
  VariantIssues,
} from 'modules/experiments/types';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  VariantFormFieldKeyToContentKeyMap,
  VariantFormFieldKeyToVariantInputKeyMap,
} from '../constants';
import {
  useAddExperimentVariant,
  useDeleteExperimentVariant,
  useExperimentsTranslations,
  useRenameExperimentVariant,
  useUpdateExperimentVariant,
} from '../hooks';
import { getTakeoverExperimentLocale } from '../takeover/ducks/selectors';
import { MutatedExperimentVariant } from '../takeover/types';
import {
  getDateTimeStampFromSeparateDateAndTime,
  getVariantFormFieldSchema,
  getVariantInputKeyAndType,
  variantAlphabet,
} from '../utils';
import { useTakeoverExperimentContext } from './TakeoverExperimentContext';
import {
  VariantConfigurationContext,
  initialVariantConfigurationContextState,
} from './VariantConfigurationContext';
import { useVariantTemplateValidationContext } from './VariantTemplateValidationContext';

export const VariantConfigurationContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { t } = useExperimentsTranslations();
  const [addingNewVariant, setAddingNewVariant] = useState(false);
  const [deletingActiveVariant, setDeletingActiveVariant] = useState(false);
  const [selectedVariantErrorCount, setSelectedVariantErrorCount] = useState(0);
  const [variantIssueCount, setVariantIssueCount] = useState(0);
  const [variantIssues, setVariantIssues] = useState<VariantIssues[]>([]);
  const {
    isSendTimeExperiment,
    sendTimeMetaInfo: { sendTimeStartTimeZone },
  } = useTakeoverExperimentContext();
  const variantList = useSelector(getTakeoverVariantList);
  const experimentId = useSelector(getTakeoverExperimentId);
  const type = useSelector(getTakeoverExperimentType);
  const messageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const campaignState = useSelector(getTakeoverCampaignState);
  const currentLocale = useSelector(getTakeoverExperimentLocale);
  const [selectedVariant, setSelectedVariant] = useState(
    initialVariantConfigurationContextState.selectedVariant,
  );
  const selectedVariantRef = useRef(initialVariantConfigurationContextState.selectedVariant);
  const [selectMode, setSelectMode] = useState(initialVariantConfigurationContextState.selectMode);
  const { formFieldSchema, formFieldZodSchema } = useMemo(
    () => getVariantFormFieldSchema({ t, type, messageMedium }),
    [t, type, messageMedium],
  );
  const variantInputKeyAndType = getVariantInputKeyAndType(messageMedium, type);
  const variantIdentifierString = variantList.reduce(
    (acc, variant) =>
      `${acc}-${variant.id}${Object.values(variant.content).filter(Boolean).join('')}`,
    '',
  );

  const { currentLocalesTemplateError, updateTemplateErrorsForAllVariantLocales } =
    useVariantTemplateValidationContext();
  const updateExperimentVariantWithErrorToaster = useUpdateExperimentVariant({
    experimentId,
    showErrorToaster: true,
  });
  const updateExperimentVariantWithoutErrorToaster = useUpdateExperimentVariant({
    experimentId,
    showErrorToaster: false,
  });
  const renameExperimentVariant = useRenameExperimentVariant({ experimentId });
  const deleteExperimentVariant = useDeleteExperimentVariant({ experimentId });
  const createExperimentVariant = useAddExperimentVariant({ experimentId });

  const setEditSelectMode = useCallback((variant: MutatedExperimentVariant) => {
    setSelectMode(SelectVariantMode.EDIT);
    setSelectedVariant(variant);
    selectedVariantRef.current = variant;
  }, []);

  const setPreviewSelectMode = useCallback((variant: MutatedExperimentVariant) => {
    setSelectMode(SelectVariantMode.PREVIEW);
    setSelectedVariant(variant);
    selectedVariantRef.current = variant;
  }, []);

  const copyControlVariant = (variantName: string, locale: string) =>
    createExperimentVariant(variantList?.[0]?.id, locale, variantName);

  const getPrecedingVariant = useCallback(
    (variantId: number) =>
      variantList[variantList.findIndex((variant) => variant.id === variantId) - 1],
    [variantList.length],
  );

  const addNewVariant = useCallback(async () => {
    setAddingNewVariant(true);
    const variantName = variantAlphabet(variantList.length, t('variantCard.prefix.variant'));
    const response = await copyControlVariant(variantName, currentLocale);
    if (response.isSuccess) {
      setEditSelectMode(response.mutatedVariant);
      updateTemplateErrorsForAllVariantLocales(response.mutatedVariant.id);
    }
    setAddingNewVariant(false);
  }, [variantList.length, currentLocale]);

  const toggleToPreviewSelectMode = useCallback(() => {
    setSelectMode(SelectVariantMode.PREVIEW);
    setSelectedVariant(selectedVariantRef.current);
  }, []);

  const updateSelectedVariantContent = useCallback(
    (fieldName: VariantFormFieldKey, fieldValue: string) => {
      setSelectedVariant((previousValue) =>
        previousValue
          ? {
              ...previousValue,
              content: {
                ...previousValue.content,
                [VariantFormFieldKeyToContentKeyMap[fieldName]]: fieldValue,
              },
            }
          : null,
      );
    },
    [],
  );

  const variantInputValueForTemplateContent = (variantFieldValues: VariantFieldValues) => {
    return Object.entries<Record<VariantFormFieldKey, any>>(variantFieldValues).reduce(
      (acc, [key, value]) => {
        const variantInputKey = VariantFormFieldKeyToVariantInputKeyMap[key as VariantFormFieldKey];
        return {
          ...acc,
          [variantInputKey]: value,
        };
      },
      {},
    );
  };

  const variantInputValueForSendTime = (variantFieldValues: VariantFieldValues) => {
    const { sendTimeDate, sendTimeTime } = variantFieldValues;
    const variantInputKey =
      VariantFormFieldKeyToVariantInputKeyMap[VariantFormFieldKey.SEND_TIME_DATE];

    return {
      [variantInputKey]: getDateTimeStampFromSeparateDateAndTime(
        sendTimeDate,
        sendTimeTime,
        sendTimeStartTimeZone,
      ).toISOString(),
    };
  };

  const getUpdateVariantArgs = useCallback(
    (variantId: number, variantFieldValues: VariantFieldValues) => {
      if (variantInputKeyAndType) {
        const { variantInputKey, variantInputType } = variantInputKeyAndType;

        return {
          variationId: variantId,
          variationType: variantInputType,
          [variantInputKey]:
            variantInputType === ExperimentVariationInputType.SEND_TIME
              ? variantInputValueForSendTime(variantFieldValues)
              : variantInputValueForTemplateContent(variantFieldValues),
        } as UpdateVariantArgs;
      }
      return {} as UpdateVariantArgs;
    },
    [variantInputKeyAndType],
  );

  /**
   * Set the selected variant once the
   * variant list loads on page load.
   */
  useEffect(() => {
    if (variantList.length > 0) {
      if (
        isSendTimeExperiment &&
        campaignState !== CampaignState.Scheduled &&
        variantList.length === 1
      ) {
        /**
         * We default the control's send time for a non scheduled campaign
         * to two days from the experiment creation day. This will open
         * the control variant of a send time experiment in edit mode.
         * Allowing users to easily modify the default value
         */
        setEditSelectMode(variantList[0]);
      } else {
        setPreviewSelectMode(variantList[0]);
      }
    }
  }, [variantList.length > 0]);

  /**
   * The selected variant object needs to be updated
   * if the content in the variant list updates.
   * This can happen because of a refetch of the variant list
   */
  useEffect(() => {
    const newSelectedVariant = variantList.find(({ id }) => selectedVariant?.id === id);
    setSelectedVariant((prevSelectedVariant) => {
      const updatedSelectedVariant = newSelectedVariant || prevSelectedVariant;
      selectedVariantRef.current = updatedSelectedVariant;
      return updatedSelectedVariant;
    });
  }, [variantIdentifierString]);

  /** Computes the error count for the current variant which is selected */
  useEffect(() => {
    const selectedTemplateErrors = currentLocalesTemplateError[selectedVariant?.id ?? 0];

    if (!selectedTemplateErrors) {
      setSelectedVariantErrorCount(0);
      return;
    }

    const totalErrors = Object.values(selectedTemplateErrors).reduce(
      (acc, arr) => acc + arr.length,
      0,
    );
    setSelectedVariantErrorCount(totalErrors);
  }, [currentLocalesTemplateError[selectedVariant?.id ?? 0]]);

  /** Computes the error count and aggregates the errors across variants */
  useEffect(() => {
    const { issueCount, issues } = variantList.reduce(
      (aggregateIssues, currVariant) => {
        const currentVariantTemplateIssues = currentLocalesTemplateError[currVariant.id];

        if (!currentVariantTemplateIssues) {
          return aggregateIssues;
        }

        /** Aggregating the content and design error from each variant */
        const aggregateVariantIssues = Object.values(currentVariantTemplateIssues).reduce(
          (acc, arr) => {
            return {
              issueCount: acc.issueCount + arr.length,
              issueList: [
                ...acc.issueList,
                ...arr.map(({ errorMessage = '', subMessage = '' }) => ({
                  errorMessage,
                  subMessage,
                })),
              ],
            };
          },
          { issueCount: 0, issueList: [] as VariantIssue[] },
        );

        if (!aggregateVariantIssues.issueList.length) return aggregateIssues;

        return {
          issueCount: aggregateIssues.issueCount + aggregateVariantIssues.issueCount,
          issues: [
            ...aggregateIssues.issues,
            {
              alphabet: currVariant.alphabet,
              bgColor: currVariant.bgColor,
              textColor: currVariant.textColor,
              name: currVariant.name,
              issueList: aggregateVariantIssues.issueList,
            },
          ],
        };
      },
      { issueCount: 0, issues: [] as VariantIssues[] },
    );

    setVariantIssueCount(issueCount);
    setVariantIssues(issues);
  }, [currentLocalesTemplateError, variantList.length]);

  const context: VariantConfigurationContextState = {
    isEditSelectMode: selectMode === SelectVariantMode.EDIT,
    isPreviewSelectMode: selectMode === SelectVariantMode.PREVIEW,
    hasMaximumNumberOfVariants: variantList.length > 9,
    selectedVariant,
    selectedVariantErrorCount,
    variantIssueCount,
    variantIssues,
    selectMode,
    formFieldSchema,
    formFieldZodSchema,
    variantList,
    addingNewVariant,
    deletingActiveVariant,
    setDeletingActiveVariant,
    addNewVariant,
    updateExperimentVariant: (
      variantId: number,
      variantFieldValues: VariantFieldValues,
      showErrorToaster = true,
      updateRedux = true,
    ) => {
      const updateExperimentVariant = showErrorToaster
        ? updateExperimentVariantWithErrorToaster
        : updateExperimentVariantWithoutErrorToaster;
      return updateExperimentVariant(
        { ...getUpdateVariantArgs(variantId, variantFieldValues), locale: currentLocale },
        updateRedux,
      );
    },
    renameExperimentVariant,
    deleteExperimentVariant,
    getPrecedingVariant,
    setEditSelectMode,
    setPreviewSelectMode,
    toggleToPreviewSelectMode,
    updateSelectedVariantContent,
  };

  return (
    <VariantConfigurationContext.Provider value={context}>
      {children}
    </VariantConfigurationContext.Provider>
  );
};
